// PR-31: per-response `x-request-id` capture for FE↔BE error correlation.
//
// The server (request_id.rs) stamps every response with `x-request-id`,
// stitched into the tracing span so each backend log line carries that id.
// We mirror it on the frontend: every successful fetch pushes the id
// onto a small ring buffer; when an error fires, `reportClientError`
// dumps the most recent entries into the error row's `context`. Admin
// tooling can then jump from a frontend error → backend log span by
// grepping the id.
//
// Why a ring buffer (not a Map keyed by URL): the call site that
// catches the error often doesn't know which exact URL fired. Keeping
// the last N (in time order) lets us include all candidates and let
// the human triager pick the relevant one. N is small enough that the
// row stays tiny.
//
// Lives in its own module to break the cyclic-import risk between
// `api.ts` (writes) and `error_capture.ts` (reads).

export interface RequestIdEntry {
  /** URL pathname that produced this id — full URL is too noisy. */
  path: string
  /** The id verbatim from the response header. */
  id: string
  /** ms since epoch — lets the triager order entries vs the error timestamp. */
  t: number
  /** HTTP status of the response that carried the id. Helps spot
   *  "the failing request was the one with status=500" vs "the last
   *  successful one was unrelated". */
  status: number
}

const RING: RequestIdEntry[] = []
const MAX = 10

export function pushRequestId(entry: Omit<RequestIdEntry, 't'>): void {
  RING.push({ ...entry, t: Date.now() })
  if (RING.length > MAX) RING.shift()
}

export function recentRequestIds(): RequestIdEntry[] {
  return [...RING]
}
